import { Spacing } from '@persona-id/design-tokens';
import { TransferInIcon, TransferOutIcon } from '@persona-id/design-tokens/icons';
import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import FileUpload from 'dashboard/components-lib/FileUpload';
import { FileWithPreview } from 'dashboard/components-lib/FileUpload/types';
import Form from 'dashboard/layouts/FormLayout/Form';
import {
  ReportTemplate,
  ReportTemplateFilterOption,
  SUPPORTED_BULK_UPLOAD_REPORT_TEMPLATE_TYPES,
} from 'dashboard/models/report-template';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { ApiModel } from 'lib/interfaces/api';
import { useFormSecurely as useForm } from 'lib/security/reactHookForm';

const FileUploadLabelContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const List = styled.ul`
  padding: ${Spacing[4]};
`;

enum Page {
  DownloadCSV = 'download_csv',
  UploadCSV = 'upload_csv',
}

interface CsvMeta {
  columns: string[];
  csvUrl: string;
}

const REPORT_TEMPLATE_CSV_META: { [key in ReportTemplate['type']]?: CsvMeta } = {
  [ApiModel.ReportTemplateAddressLookup]: {
    columns: [
      'Reference ID (optional)',
      'Address Street 1',
      'Address Street 2 (optional)',
      'Address City',
      'Address Subdivision',
      'Address Postal Code',
      'Address Country Code',
    ],
    csvUrl: '/csvs/address-lookup/bulk.csv',
  },
  [ApiModel.ReportTemplateAdverseMedia]: {
    columns: [
      'Reference ID (optional)',
      'First name',
      'Last name',
      'Birthdate YYYY-MM-DD (optional)',
      'Country Code (optional)',
    ],
    csvUrl: '/csvs/adverse-media/bulk.csv',
  },
  [ApiModel.ReportTemplateBusinessAdverseMedia]: {
    columns: ['Reference ID (optional)', 'Account Type Id (optional)', 'Business Name'],
    csvUrl: '/csvs/business-adverse-media/bulk.csv',
  },
  [ApiModel.ReportTemplateBusinessLookup]: {
    columns: [
      'Reference ID (optional)',
      'Account Type Id (optional)',
      'Business Name',
      'Address Street 1',
      'Address Street 2 (optional)',
      'Address City',
      'Address Subdivision',
      'Address Postal Code',
      'Associated Person Name First (optional)',
      'Associated Person Name Last (optional)',
      'Phone Number (optional)',
      'Website (optional)',
    ],
    csvUrl: '/csvs/business-lookup/bulk.csv',
  },
  [ApiModel.ReportTemplateBusinessWatchlist]: {
    columns: [
      'Reference ID (optional)',
      'Account Type Id (optional)',
      'Business Name',
      'Country Code (optional)',
    ],
    csvUrl: '/csvs/business-watchlist/bulk.csv',
  },
  [ApiModel.ReportTemplateEmailAddress]: {
    columns: ['Reference ID (optional)', 'Email address'],
    csvUrl: '/csvs/email-address/bulk.csv',
  },
  [ApiModel.ReportTemplatePhoneNumber]: {
    columns: ['Reference ID (optional)', 'Phone number'],
    csvUrl: '/csvs/phone-number/bulk.csv',
  },
  [ApiModel.ReportTemplatePoliticallyExposedPerson]: {
    columns: [
      'Reference ID (optional)',
      'First name',
      'Last name',
      'Birthdate YYYY-MM-DD (optional)',
      'Country Code (optional)',
    ],
    csvUrl: '/csvs/politically-exposed-person/bulk.csv',
  },
  [ApiModel.ReportTemplateProfileNonAuthoritative]: {
    columns: [
      'Reference ID (optional)',
      'Email address',
      'Phone number',
      'First name',
      'Last name',
      'Locality',
      'Subdivision',
    ],
    csvUrl: '/csvs/profile-non-authoritative/bulk.csv',
  },
  [ApiModel.ReportTemplateWatchlist]: {
    columns: [
      'Reference ID (optional)',
      'First name',
      'Last name',
      'Birthdate YYYY-MM-DD (optional)',
      'Country Code (optional)',
    ],
    csvUrl: '/csvs/watchlist/bulk_person.csv',
  },
};

interface FormCsvUploadCreateReportProps {
  reportTemplateFilterOption: ReportTemplateFilterOption;
  onSubmit: (data: FormCsvUploadCreateReportData) => void;
}

export interface FormCsvUploadCreateReportData {
  file: FileWithPreview;
}

export const FormCsvUploadCreateReport = ({
  reportTemplateFilterOption,
  onSubmit,
}: FormCsvUploadCreateReportProps) => {
  const [page, setPage] = useState(Page.DownloadCSV);
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty, isValid },
  } = useForm<FormCsvUploadCreateReportData>({ mode: 'onChange' });

  if (!SUPPORTED_BULK_UPLOAD_REPORT_TEMPLATE_TYPES.includes(reportTemplateFilterOption.type)) {
    return <span>Upload CSV feature unavailable for {reportTemplateFilterOption.name}.</span>;
  }

  if (page === Page.DownloadCSV) {
    return (
      <>
        <h3>Report input</h3>
        <span>Please include the following input in your CSV file</span>
        <List>
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          {REPORT_TEMPLATE_CSV_META[reportTemplateFilterOption.type].columns.map((columnName) => (
            <li key={columnName}>{columnName}</li>
          ))}
        </List>

        <Spacer height={2} />
        <Link
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          to={REPORT_TEMPLATE_CSV_META[reportTemplateFilterOption.type]?.csvUrl}
          target='_blank'
          download
        >
          <Button variant='basic' prefixIcon={<TransferInIcon />}>
            Download CSV template
          </Button>
        </Link>
        <Spacer height={1} />
        <Button
          id='form-csv-upload-create-report__next-button'
          variant='primary'
          onClick={() => setPage(Page.UploadCSV)}
        >
          Next
        </Button>
      </>
    );
  }

  return (
    <>
      <h3>CSV Format Requirements</h3>
      <List>
        <li>
          Matches the headers provided in the CSV template for the report name and selected input(s)
        </li>
        <li>Each record contains the required inputs (if there are more than one)</li>
        <li>Contains no more than 10,000 rows</li>
      </List>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          control={control}
          name='file'
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => {
            return (
              <FileUpload
                accept={['text/csv']}
                label={
                  <FileUploadLabelContainer data-test='form-csv-upload-create-report__file-upload'>
                    <TransferOutIcon />
                    <Spacer height={1} />
                    <p>Drag and drop your .CSV file here or</p>
                    <Spacer height={1} />
                    <Button>Select .CSV File</Button>
                  </FileUploadLabelContainer>
                }
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                preview={null}
                value={value}
                onChange={onChange}
              />
            );
          }}
        />
        <Spacer height={2} />
        <Button
          variant='basic'
          id='form-csv-upload-create-report__back-button'
          onClick={() => setPage(Page.DownloadCSV)}
        >
          Back
        </Button>
        <Spacer height={1} />
        <Button type='submit' disabled={!isDirty || !isValid || isSubmitting}>
          Create Reports
        </Button>
      </Form>
    </>
  );
};
